import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

// Sensible defaults so gated UI stays useful even before the first fetch.
const DEFAULTS = {
  public_job_share: true,
  offline_sync_heartbeat: true,
  voice_notes: false,
  estimate_signature: false,
  whatsapp_share: true,
};

const PlatformSettingsContext = createContext({
  features: DEFAULTS,
  company: {},
  support: {},
  numbering: {},
  loaded: false,
  reload: () => {},
  isEnabled: (k) => !!DEFAULTS[k],
});

export function PlatformSettingsProvider({ children }) {
  const [data, setData] = useState({ features: DEFAULTS, company: {}, support: {}, numbering: {}, loaded: false });

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/admin/settings");
      setData({
        features: { ...DEFAULTS, ...(r.data?.features || {}) },
        company: r.data?.company || {},
        support: r.data?.support || {},
        numbering: r.data?.numbering || {},
        loaded: true,
      });
    } catch (e) {
      // Non-admins get a 403 — feature flags fall back to public /api/config-lite
      try {
        const r = await api.get("/config/features");
        setData((prev) => ({ ...prev, features: { ...DEFAULTS, ...(r.data || {}) }, loaded: true }));
      } catch (e2) {
        setData((prev) => ({ ...prev, loaded: true }));
      }
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const isEnabled = useCallback((k) => !!data.features?.[k], [data.features]);

  return (
    <PlatformSettingsContext.Provider value={{ ...data, reload, isEnabled }}>
      {children}
    </PlatformSettingsContext.Provider>
  );
}

export const usePlatformSettings = () => useContext(PlatformSettingsContext);
export const useFeatureFlag = (key) => {
  const { isEnabled } = usePlatformSettings();
  return isEnabled(key);
};
